/**
 * Settings → Reminders (REMIND-ONE-01, 2026-09-23) — every appointment
 * reminder in one place.
 *
 * Two senders, split by where the visit was booked — they never share a
 * booking:
 *   · Booked in ESC → the office server's nightly run (reminder-send.ps1,
 *     6 PM the evening before): email live since May; texts behind the
 *     org_setting `reminder_sms` switch below.
 *   · Booked in Pegasus → the Comms automations `appt_reminder` (text the
 *     day before) and `appt_confirm` (text when booked), held until Pegasus
 *     messages go live (org_setting comms_live).
 * Never twice: v_reminder_candidates skips a customer whose Pegasus reminder
 * text already went out for that date.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Mail, MessageSquare } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import { useCommsAutomations, useCommsLive, useSaveAutomation } from "@/features/comms/comms";

interface ReminderSmsSetting {
  enabled: boolean;
  mode: "no_email_only" | "everyone";
}

/** A calendar date ("2026-09-23") → "Wed, Sep 23". */
const dayLabel = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
/** A moment (timestamptz) → the office's calendar day. */
const whenLabel = (ts: string) =>
  new Date(ts).toLocaleDateString("en-US", { timeZone: "America/Chicago", weekday: "short", month: "short", day: "numeric" });
const hourLabel = (h: number) => (h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`);

/** The nightly run's latest night, from its own ledger (reminder_sent). */
function useLastNightlyRun() {
  return useQuery({
    queryKey: ["reminder_last_run"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ date: string; emails: number; texts: number; at: string | null } | null> => {
      const { data: last } = await supabase.from("reminder_sent")
        .select("appointment_date").eq("status", "sent").order("appointment_date", { ascending: false }).limit(1).maybeSingle();
      const date = (last as { appointment_date: string } | null)?.appointment_date;
      if (!date) return null;
      const { data } = await supabase.from("reminder_sent")
        .select("channel, sent_at").eq("appointment_date", date).eq("status", "sent");
      const rows = (data ?? []) as { channel: string; sent_at: string | null }[];
      const at = rows.map((r) => r.sent_at).filter(Boolean).sort().pop() ?? null;
      return { date, emails: rows.filter((r) => r.channel === "email").length, texts: rows.filter((r) => r.channel === "sms").length, at };
    },
  });
}

function Toggle({ on, disabled, onClick, title }: { on: boolean; disabled?: boolean; onClick: () => void; title: string }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} title={title}
      className={cn(
        "rounded-md px-3 py-1.5 text-xs font-semibold shadow-e1 transition-colors disabled:opacity-50",
        on ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border border-hairline bg-white text-slate-600 hover:bg-slate-50",
      )}>
      {on ? "On" : "Off"}
    </button>
  );
}

function Row({ icon: Icon, title, info, children, right }: { icon: typeof Mail; title: string; info?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="flex min-w-0 max-w-xl gap-2.5">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
            {title}
            {info && <InfoTip label={`About the ${title.toLowerCase()}`}>{info}</InfoTip>}
          </div>
          <div className="mt-0.5 text-xs leading-relaxed text-slate-500">{children}</div>
        </div>
      </div>
      {right && <div className="flex items-center gap-2">{right}</div>}
    </div>
  );
}

export function RemindersSection() {
  const canManage = useCan("settings.messages");
  const sms = useOrgSettingJson<ReminderSmsSetting>("reminder_sms", { enabled: false, mode: "no_email_only" });
  const saveSetting = useSaveOrgSetting();
  const lastRun = useLastNightlyRun().data ?? null;
  const autos = useCommsAutomations().data ?? [];
  const live = useCommsLive().data ?? false;
  const saveAuto = useSaveAutomation();
  const [flash, setFlash] = useState<string | null>(null);
  const v = sms.data ?? { enabled: false, mode: "no_email_only" as const };

  const done = (msg: string) => { setFlash(msg); window.setTimeout(() => setFlash(null), 2500); };
  const updateSms = (patch: Partial<ReminderSmsSetting>) =>
    saveSetting.mutate({ key: "reminder_sms", value: { ...v, ...patch } }, { onSuccess: () => done("Saved") });

  const remind = autos.find((a) => a.key === "appt_reminder");
  const confirm = autos.find((a) => a.key === "appt_confirm");
  const cfg = (remind?.config ?? {}) as { at_hour?: number; days_before?: number };
  const when = `${hourLabel(Number(cfg.at_hour ?? 16))} ${Number(cfg.days_before ?? 1) === 1 ? "the day before" : `${cfg.days_before} days before`}`;
  const heldChip = !live && (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500" title="Pegasus sends nothing to customers until messages go live">
      Waiting for go-live
    </span>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-hairline bg-white p-5 shadow-e1">
        <div className="mb-3 flex items-center gap-2">
          <h3 className="text-[13px] font-bold text-slate-800">Visits Booked in ESC</h3>
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Live</span>
        </div>
        <div className="divide-y divide-slate-100">
          <Row icon={Mail} title="Email reminder">
            Sent at 6 PM the evening before to customers with an email on file, by the office server.
            {lastRun && (
              <span className="mt-1 block text-slate-600">
                Last run{lastRun.at ? ` ${whenLabel(lastRun.at)}` : ""}: <strong>{lastRun.emails}</strong> email{lastRun.emails === 1 ? "" : "s"}
                {lastRun.texts > 0 && <> and <strong>{lastRun.texts}</strong> text{lastRun.texts === 1 ? "" : "s"}</>} for {dayLabel(lastRun.date)} visits.
              </span>
            )}
          </Row>
          <Row icon={MessageSquare} title="Text reminder"
            info="Sent through Podium, so replies land in the Inbox. Customers who texted STOP are never sent anything."
            right={<Toggle on={v.enabled} disabled={!canManage || saveSetting.isPending} onClick={() => updateSms({ enabled: !v.enabled })}
              title={v.enabled ? "Turn text reminders off" : "Turn text reminders on — takes effect on tonight's run"} />}>
            Same 6 PM run, from <strong>952-592-9895</strong>.
            <div className="mt-2 space-y-1">
              {([
                ["no_email_only", "Only when there's no email on file", "every visit gets some reminder"],
                ["everyone", "Everyone", "text and email both, for customers who have both"],
              ] as const).map(([key, label, hint]) => (
                <label key={key} className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1 transition-colors",
                  v.mode === key ? "bg-brand-50/60" : "hover:bg-slate-50",
                  !canManage && "cursor-not-allowed opacity-60",
                )}>
                  <input type="radio" name="reminder-sms-mode" checked={v.mode === key} disabled={!canManage || saveSetting.isPending}
                    onChange={() => updateSms({ mode: key })} className="mt-0.5 h-3.5 w-3.5 accent-brand-700" />
                  <span><span className="font-medium text-slate-700">{label}</span> <span className="text-slate-400">— {hint}</span></span>
                </label>
              ))}
            </div>
            {!v.enabled && (
              <span className="mt-2 block text-slate-500">
                Off for now. Tonight's run still lists what it <em>would</em> text, so the list can be checked first.
              </span>
            )}
          </Row>
        </div>
      </div>

      <div className="rounded-2xl border border-hairline bg-white p-5 shadow-e1">
        <div className="mb-3 flex items-center gap-2">
          <h3 className="text-[13px] font-bold text-slate-800">Visits Booked in Pegasus</h3>
          {heldChip}
        </div>
        <div className="divide-y divide-slate-100">
          <Row icon={MessageSquare} title="Text reminder"
            info="If a customer already got one for that day, they aren't sent another, even with visits booked in both ESC and Pegasus."
            right={remind && <Toggle on={remind.enabled} disabled={!canManage || saveAuto.isPending}
              onClick={() => saveAuto.mutate({ key: "appt_reminder", enabled: !remind.enabled }, { onSuccess: () => done("Saved") })}
              title={remind.enabled ? "Turn the Pegasus reminder text off" : "Turn the Pegasus reminder text on"} />}>
            Sent at {when}.
          </Row>
          <Row icon={MessageSquare} title="Booking confirmation text"
            right={confirm && <Toggle on={confirm.enabled} disabled={!canManage || saveAuto.isPending}
              onClick={() => saveAuto.mutate({ key: "appt_confirm", enabled: !confirm.enabled }, { onSuccess: () => done("Saved") })}
              title={confirm.enabled ? "Turn the booking confirmation text off" : "Turn the booking confirmation text on"} />}>
            Sent when a visit is booked or moved, with the date and arrival window.
          </Row>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3 text-xs">
          <Link to="/automations" className="inline-flex items-center gap-0.5 font-semibold text-brand-700 hover:text-brand-900">
            Change the wording or the time <ArrowUpRight className="h-3 w-3" />
          </Link>
          {flash && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">{flash} ✓</span>}
        </div>
      </div>
    </div>
  );
}
